import { CheckCircleOutlined, CloseCircleOutlined, SyncOutlined } from '@ant-design/icons';
import { Badge, Button, Col, DatePicker, Drawer, Form, Radio, Row, Timeline, Tooltip } from 'antd';
import Axios from 'axios';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getCompany, getUser } from 'redux/selectors';
import { FeatureFlag, useFeatureFlags } from 'utils/feature-flags/FeatureFlagsProvider';

import { getBartUrl } from '../../../../../../../config';

export default function BartSatellite({ onSyncFinished }) {
  const [satelliteVisibility, setSatelliteVisibility] = useState(false);
  const [defaultPeriodSelection, setDefaultPeriodSelection] = useState(null);
  const [syncStart, setSyncStart] = useState(null);
  const [syncEnd, setSyncEnd] = useState(null);
  const [rangeSelector, setRangeSelector] = useState([null, null]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [integrations, setIntegrations] = useState([]);
  const [timelineStatus, setTimelineStatus] = useState([]);
  const [pendingTimelineStatus, setPendingTimelineStatus] = useState(null);
  const [syncHistory, setSyncHistory] = useState([]);
  const [lastSync, setLastSync] = useState(null);

  const featureFlags = useFeatureFlags();

  const badgeStyle = {
    backgroundColor: 'lightgray',
    color: 'black',
  };

  const statusList = {
    reqeustSentSuccess: 'Sync Request Sent',
    reqeustFailed: 'Sync Request Failed',
    syncSuccess: 'Airtrail Sync Successful',
    syncSuccessWithIssues: 'Sync Successful with Issues',
    processingData: 'Processing Data',
    processingCompleted: 'Processing Completed',
    reloadPage: 'Reload Page To See Changes In Effect',
    fetchingUpdates: 'Fetching Updates',
    viewUpdated: 'View Updated',
  };

  const company = useSelector(getCompany);

  useEffect(() => {
    getSyncHistory();
  }, [integrations]);

  useEffect(() => {
    if (syncHistory?.length > 0) {
      setLastSync(syncHistory[0]);
    } else {
      setLastSync(null);
    }
  }, [syncHistory]);

  useEffect(() => {
    if (company?.Integrations) {
      setIntegrations(company.Integrations);
      // set default range for Range selector
      setDefaultPeriodSelection(7);
    }
  }, [company]);

  useEffect(() => {
    if (defaultPeriodSelection && defaultPeriodSelection !== 'custom') {
      const halfTime = Math.floor(defaultPeriodSelection / 2);
      const end = moment()
        .endOf('day')
        .add(halfTime, 'days');
      const start = end
        .clone()
        .subtract(defaultPeriodSelection - 1, 'days')
        .startOf('day');
      setSyncStart(start);
      setSyncEnd(end);
      setRangeSelector([start, end]);
    }
  }, [defaultPeriodSelection]);

  const bartSatelliteVisibility = value => {
    setSatelliteVisibility(value);
  };

  const handleDefaultPeriodChange = e => {
    setDefaultPeriodSelection(e.target.value);
  };

  const handleDateRangeChange = (date, dateString) => {
    setRangeSelector(date);
    if (date && date[0] && date[1]) {
      setSyncStart(moment(date[0]).startOf('day'));
      setSyncEnd(moment(date[1]).endOf('day'));
      setDefaultPeriodSelection('custom');
    }
  };

  const getIntegrationOf = type => {
    if (integrations.length > 0) {
      const filter = integrations.filter(int => int.Type === type);
      if (filter.length > 0) {
        return filter[0];
      }
      return null;
    }
    return null;
  };

  const pushTimelineStatus = status => {
    setTimelineStatus(pre => {
      return [...pre, JSON.parse(JSON.stringify(status))];
    });
    setPendingTimelineStatus(JSON.parse(JSON.stringify(status)));
  };

  const clearTimeline = () => {
    setTimelineStatus([]);
  };

  const handleSyncRequest = () => {
    const integration = getIntegrationOf('bart-sync');
    if (integration) {
      const params = {
        ApiKey: integration.ApiKey,
        IntegrationLayerKey: integration.IntegrationLayerKey,
        Identifier: integration.Identifier,
        CreateNewUsers: integration?.CreateNewUsers || false,
        start: syncStart
          .startOf('day')
          .utc()
          .format('YYYY-MM-DD[T]HH:mm:ss.SSS[Z]'),
        end: syncEnd
          .endOf('day')
          .utc()
          .format('YYYY-MM-DD[T]HH:mm:ss.SSS[Z]'),
      };

      pushTimelineStatus(`${statusList.reqeustSentSuccess}`);

      Axios({
        method: 'get',
        url: `${getBartUrl()}/v1/bart/crew-activities/fetch/${integration.Identifier}`,
        params,
      })
        .then(res => {
          if (res.status === 200) {
            pushTimelineStatus(statusList.syncSuccess);
            pushTimelineStatus(statusList.processingData);

            setTimeout(() => {
              pushTimelineStatus(statusList.processingCompleted);

              // set the status count
              if (res.data) {
                const plannedRes = res.data.ScheduleSync;
                const actualDutyRes = res.data.DutyTimeSync;

                if (plannedRes) {
                  pushTimelineStatus(
                    `Planned Duty Time -> Created: ${plannedRes.Created} | Updated: ${plannedRes.Updated} | Deleted: ${plannedRes.Deleted}`,
                  );
                }

                if (actualDutyRes) {
                  pushTimelineStatus(
                    `Actual Duty Time -> Created: ${actualDutyRes.Created} | Updated: ${actualDutyRes.Updated} | Deleted: ${actualDutyRes.Deleted}`,
                  );
                }
              }

              pushTimelineStatus(statusList.fetchingUpdates);
              getSyncHistory();
              onSyncFinished();
              pushTimelineStatus(statusList.viewUpdated);
              setIsSyncing(false);
              setPendingTimelineStatus(false);
            }, 10000);
            // const {data} = res
            // console.log(data)
          } else {
            pushTimelineStatus(statusList.reqeustFailed);
            setIsSyncing(false);
          }
        })
        .catch(err => {
          console.log(err);
          pushTimelineStatus(statusList.reqeustFailed);
          setIsSyncing(false);
          setPendingTimelineStatus(false);
        });
    }
  };

  const getSyncHistory = () => {
    const integration = getIntegrationOf('bart-sync');
    if (integrations.length > 0 && integration?.Enabled) {
      const params = {
        ApiKey: integration.ApiKey,
        IntegrationLayerKey: integration.IntegrationLayerKey,
        Identifier: integration.Identifier,
      };

      Axios({
        method: 'get',
        url: `${getBartUrl()}/v1/bart/sync-history/${integration.Identifier}`,
        params,
      }).then(res => {
        setSyncHistory(res.data);
      });
    }
  };

  return (
    <>
      {featureFlags.get(FeatureFlag.BartSyncIntegration) ? (
        <>
          {integrations.length > 0 && getIntegrationOf('bart-sync')?.Enabled && (
            <Tooltip
              title={
                lastSync
                  ? `Last sync: ${moment(lastSync.SyncTime).fromNow()}`
                  : 'Last sync not found'
              }
            >
              <Button
                type="primary"
                onClick={() => {
                  bartSatelliteVisibility(true);
                }}
              >
                <SyncOutlined /> Sync With BART
              </Button>
            </Tooltip>
          )}
        </>
      ) : null}

      <Drawer
        width={600}
        title="BART Sync"
        placement="right"
        onClose={() => {
          bartSatelliteVisibility(false);
        }}
        visible={satelliteVisibility}
      >
        <>
          <Row>
            <Form layout="vertical">
              <Form.Item label="Choose Default Period" name="defaultPeriodSelection">
                <Radio.Group onChange={handleDefaultPeriodChange} value={defaultPeriodSelection}>
                  <Radio.Button value={1}>Today</Radio.Button>
                  <Radio.Button value={7}>1 Week</Radio.Button>
                  <Radio.Button value={15}>2 Weeks</Radio.Button>
                </Radio.Group>
              </Form.Item>

              <Form.Item label="Custom Range">
                <DatePicker.RangePicker
                  onChange={handleDateRangeChange}
                  value={rangeSelector}
                  allowClear
                />
              </Form.Item>
            </Form>
          </Row>

          <Row>
            <Col span={24}>
              <Button
                loading={isSyncing}
                type="primary"
                icon={<SyncOutlined />}
                size="large"
                onClick={() => {
                  setIsSyncing(true);
                  clearTimeline();
                  handleSyncRequest();
                }}
              >
                Sync With BART
              </Button>
            </Col>
          </Row>

          <Row style={{ marginTop: '20px' }}>
            {lastSync && (
              <Col>
                <h3>
                  Last sync on{' '}
                  <Badge
                    style={badgeStyle}
                    count={moment(lastSync.SyncTime).format('MMMM Do YYYY, h:mm:ss a')}
                  />{' '}
                </h3>
                <Row>
                  <h3>
                    From{' '}
                    <Badge
                      style={badgeStyle}
                      count={moment(lastSync.RangeStart).format('MMMM Do YYYY, h:mm:ss a')}
                    />{' '}
                  </h3>
                </Row>
                <Row>
                  <h3>
                    To{' '}
                    <Badge
                      style={badgeStyle}
                      count={moment(lastSync.RangeEnd).format('MMMM Do YYYY, h:mm:ss a')}
                    />{' '}
                  </h3>
                </Row>
              </Col>
            )}
          </Row>

          <div style={{ marginTop: '30px' }}>
            <Timeline pending={pendingTimelineStatus}>
              {timelineStatus.map((status, index) =>
                status === statusList.reqeustFailed ? (
                  <Timeline.Item dot={<CloseCircleOutlined />} color="red">
                    {status}
                  </Timeline.Item>
                ) : (
                  <Timeline.Item dot={<CheckCircleOutlined />} color="green">
                    {status}
                  </Timeline.Item>
                ),
              )}
            </Timeline>
          </div>
        </>
      </Drawer>
    </>
  );
}
